import React, { useState, useEffect, useCallback } from "react";
import * as S from "./CadastroPageStyle";
import {
  formatCPF,
  formatCEP,
  formatTelefone,
  formatCNPJ,
  removeFormatting,
} from "../../assets/utils";
import MessageBox from "./MessageBox";
import { usePulse } from "../../context/LoadContext";
import {
  FiArrowLeft,
  FiUser,
  FiBriefcase,
  FiGlobe,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiEye,
  FiEyeOff,
  FiTool,
} from "react-icons/fi";
import debounce from "lodash.debounce";
import { clientService } from "../../Services/clientService";
import { cepService } from "../../Services/cepService";
import docsService from "../../Services/documentValidator";

// Cadastro temporariamente desativado (manutenção). Mude para false para liberar o cadastro novamente.
const CADASTRO_EM_MANUTENCAO = true;

// Função helper para verificar se a data corresponde a 18 anos ou mais
const isOfAge = (dateString) => {
  if (!dateString) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  const birthDate = new Date(`${dateString}T00:00:00`);

  return birthDate <= eighteenYearsAgo;
};

export default function CadastroPage() {
  // Estados do formulário
  const [documentType, setDocumentType] = useState("CPF");
  const [documentValue, setDocumentValue] = useState("");
  const [nome, setNome] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [telefone, setTelefone] = useState("");
  const [pais, setPais] = useState("Brasil");
  const [cep, setCep] = useState("");
  const [endereco, setEndereco] = useState("");
  const [enderecoNumber, setEnderecoNumber] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [email, setEmail] = useState("");
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");

  // Estados de visualização de senha
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados de controle da UI
  const [resposta, setResposta] = useState(null);
  const { showPulse, hidePulse } = usePulse();
  const [documentStatus, setDocumentStatus] = useState("idle"); // idle, validating, valid, invalid
  const [documentError, setDocumentError] = useState("");
  const [usernameStatus, setUsernameStatus] = useState({
    status: "idle",
    message: "",
  });
  const [ageError, setAgeError] = useState("");

  // Estado para controlar a entrada manual
  const [isManualEntryEnabled, setIsManualEntryEnabled] = useState(false);

  // Estados do consultor (vindo da URL)
  const [consultantId, setConsultantId] = useState(null);
  const [consultantName, setConsultantName] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get("ref");
    const refName = urlParams.get("consultor");
    if (refId) {
      setConsultantId(refId);
      if (refName) setConsultantName(refName);
    }
  }, []);

  // --- FUNÇÃO DE VALIDAÇÃO ---
  const debouncedDocumentCheck = useCallback(
    debounce(async (docValue, docType) => {
      setDocumentStatus("validating");
      setDocumentError("");
      setAgeError("");
      setIsManualEntryEnabled(false);
      setNome("");
      setDataNascimento("");

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Timeout")), 7000);
      });

      const validationPromise = (async () => {
        if (docType === "CPF") {
          return await docsService.validateCPF(docValue);
        } else if (docType === "CNPJ") {
          return await docsService.validateCNPJ(docValue);
        }
        return null;
      })();

      try {
        const validationResult = await Promise.race([
          validationPromise,
          timeoutPromise,
        ]);

        if (validationResult && validationResult.success) {
          if (docType === "CPF" && !isOfAge(validationResult.birthDate)) {
            setDataNascimento(validationResult.birthDate);
            throw new Error(
              "É necessário ter 18 anos ou mais para se cadastrar."
            );
          }

          setNome(validationResult.name);
          setDataNascimento(validationResult.birthDate);

          if (docType === "CNPJ" && validationResult.address) {
            const addr = validationResult.address;
            setCep(addr.cep || "");
            setEndereco(addr.street || "");
            setEnderecoNumber(addr.number || "");
            setBairro(addr.neighborhood || "");
            setCidade(addr.city || "");
            setEstado(addr.state || "");
          }
          setDocumentStatus("valid");
        } else {
          throw new Error(
            validationResult.message || "Documento inválido ou não encontrado."
          );
        }
      } catch (error) {
        if (error.message === "Timeout") {
          setDocumentError(
            "Dados não encontrados. Por favor, preencha manualmente."
          );
          setIsManualEntryEnabled(true);
          setDocumentStatus("valid");
        } else {
          setDocumentStatus("invalid");
          if (error.message.includes("18 anos")) {
            setAgeError(error.message);
            setDocumentError("");
          } else {
            setDocumentError(error.message || "Erro ao validar documento.");
          }
        }
      }
    }, 800),
    []
  );

  useEffect(() => {
    if (documentType === "Passport") return;
    const cleanedDoc = documentValue.replace(/\D/g, "");
    const requiredLength = documentType === "CPF" ? 11 : 14;

    if (cleanedDoc.length < requiredLength) {
      setDocumentStatus("idle");
      setNome("");
      setDataNascimento("");
      setDocumentError("");
      setAgeError("");
      setIsManualEntryEnabled(false);
      return;
    }
    if (cleanedDoc.length === requiredLength) {
      debouncedDocumentCheck(cleanedDoc, documentType);
    }
  }, [documentValue, documentType, debouncedDocumentCheck]);

  useEffect(() => {
    if (documentType === "Passport" && dataNascimento) {
      const ageValidationMessage = isOfAge(dataNascimento)
        ? ""
        : "É necessário ter 18 anos ou mais para se cadastrar.";
      setAgeError(ageValidationMessage);
    } else if (isManualEntryEnabled && dataNascimento.length === 10) {
      const ageValidationMessage = isOfAge(dataNascimento)
        ? ""
        : "É necessário ter 18 anos ou mais para se cadastrar.";
      setAgeError(ageValidationMessage);
    }
  }, [dataNascimento, documentType, isManualEntryEnabled]);

  const handleDocTypeChange = (newType) => {
    setDocumentType(newType);
    setDocumentValue("");
    setNome("");
    setDataNascimento("");
    setDocumentStatus("idle");
    setDocumentError("");
    setAgeError("");
    setIsManualEntryEnabled(false);
  };

  const debouncedUsernameCheck = useCallback(
    debounce(async (username) => {
      if (!username.trim() || username.trim().length < 3) {
        setUsernameStatus({ status: "idle", message: "" });
        return;
      }
      setUsernameStatus({ status: "checking", message: "Verificando..." });
      try {
        const data = await clientService.verifyUsername(username.trim());
        setUsernameStatus(
          data.isAvailable
            ? { status: "available", message: "Disponível" }
            : { status: "taken", message: "Indisponível" }
        );
      } catch (error) {
        setUsernameStatus({ status: "error", message: "Erro ao verificar" });
      }
    }, 500),
    []
  );

  useEffect(() => {
    debouncedUsernameCheck(usuario.trim());
  }, [usuario, debouncedUsernameCheck]);

  useEffect(() => {
    if (endereco || (documentType === "CNPJ" && !isManualEntryEnabled)) return;
    const buscarEndereco = async () => {
      const cepLimpo = cep.replace(/\D/g, "");
      if (cepLimpo.length === 8) {
        try {
          const data = await cepService.getAddressByCEP(cepLimpo);
          setEndereco(data.logradouro || "");
          setBairro(data.bairro || "");
          setCidade(data.localidade || "");
          setEstado(data.uf || "");
        } catch (error) {
          setResposta({
            message: error.message || "Erro ao buscar CEP.",
            type: "error",
          });
        }
      }
    };
    buscarEndereco();
  }, [cep, endereco, documentType, isManualEntryEnabled]);

  const handleDocumentInputChange = (e) => {
    const { value } = e.target;
    let formattedValue = value;
    if (documentType === "CPF") formattedValue = formatCPF(value);
    else if (documentType === "CNPJ") formattedValue = formatCNPJ(value);
    setDocumentValue(formattedValue);
  };

  const handleCadastro = async (e) => {
    e.preventDefault();

    if (ageError) {
      setResposta({ message: ageError, type: "error" });
      return;
    }
    if (documentType !== "Passport" && documentStatus !== "valid") {
      setResposta({
        message: "Por favor, valide seu documento antes de continuar.",
        type: "error",
      });
      return;
    }

    // --- DEFINIÇÃO DAS REGRAS DE VALIDAÇÃO ---
    const validations = [
      { check: !nome, message: "O nome completo é obrigatório." },
      { check: !documentValue, message: "O documento é obrigatório." },
      {
        check: !dataNascimento,
        message: "A data de nascimento é obrigatória.",
      },
      { check: !telefone, message: "O telefone é obrigatório." },
      { check: !pais, message: "O país é obrigatório." },
      { check: !cep, message: "O CEP é obrigatório." },
      { check: !endereco, message: "O endereço é obrigatório." },
      {
        check: !enderecoNumber,
        message: "O número do endereço é obrigatório.",
      },
      { check: !bairro, message: "O bairro é obrigatório." },
      { check: !cidade, message: "A cidade é obrigatória." },
      { check: !estado, message: "O estado é obrigatório." },
      { check: !email, message: "O e-mail é obrigatório." },
      { check: !usuario, message: "O nome de usuário é obrigatório." },
      { check: !senha, message: "A senha é obrigatória." },
      { check: senha !== confirmarSenha, message: "As senhas não coincidem." },
      {
        check: senha.length < 6,
        message: "A senha deve ter no mínimo 6 caracteres.",
      },
      {
        // Regex para checar se tem APENAS números
        check: /^\d+$/.test(senha),
        message: "A senha não pode conter apenas números.",
      },
      {
        // Regex para checar se existe algum espaço em branco em qualquer lugar
        check: /\s/.test(senha),
        message: "A senha não pode conter espaços.",
      },
      {
        // --- CORREÇÃO ---
        // Regex de Whitelist: Verifica se a senha contém APENAS:
        // a-z, A-Z, 0-9, e símbolos comuns.
        // O acento circunflexo (^) no início dentro do [] nega a lista.
        // Então: se encontrar qualquer coisa que NÃO esteja nessa lista (como 'ã', 'ç'), o test retorna true.
        check: /[^a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(senha),
        message:
          "A senha contém caracteres inválidos (como 'ã', 'ç' ou emojis). Use apenas letras padrão, números e símbolos.",
      },
      {
        check: usernameStatus.status === "taken",
        message:
          "Este nome de usuário já foi escolhido. Por favor, selecione outro.",
      },
    ];

    for (const validation of validations) {
      if (validation.check) {
        setResposta({ message: validation.message, type: "error" });
        return;
      }
    }

    showPulse();
    try {
      const usernameData = await clientService.verifyUsername(usuario.trim());
      if (!usernameData.isAvailable) {
        throw new Error(
          "Este nome de usuário já foi escolhido. Por favor, selecione outro."
        );
      }

      const clientData = {
        cpfCnpj: documentValue.replace(/\D/g, ""),
        address: {
          street: endereco.toUpperCase(),
          number: enderecoNumber,
          neighborhood: bairro.toUpperCase(),
          city: cidade.toUpperCase(),
          state: estado.toUpperCase(),
          zipcode: cep,
          country: pais.toUpperCase(),
        },
        email: email.toLowerCase(),
        birthDate: dataNascimento,
        name: nome.toUpperCase(),
        password: senha.trim(), // Mantém o trim ao salvar
        username: usuario.trim(),
        phoneNumber: removeFormatting("telefone", telefone),
      };

      if (consultantId) {
        clientData.consultorId = consultantId;
        if (consultantName) {
          clientData.consultantName = consultantName;
        }
      }

      const response = await clientService.registerClient(clientData);
      setResposta({
        message:
          response.message ||
          "Usuário criado com sucesso! Você será redirecionado.",
        type: "success",
      });

      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (error) {
      setResposta({
        message:
          error.response?.data?.message ||
          error.message ||
          "Erro ao criar cadastro.",
        type: "error",
      });
    } finally {
      hidePulse();
    }
  };

  if (CADASTRO_EM_MANUTENCAO) {
    return (
      <S.PageContainer>
        <S.FormWrapper as="div">
          <S.BackButton
            type="button"
            onClick={() => (window.location.href = "/")}
          >
            <FiArrowLeft /> Voltar
          </S.BackButton>
          <S.Header style={{ marginBottom: 0 }}>
            <S.Logo src="/logo-golden.png" alt="Golden Brasil" />
            <FiTool
              size={40}
              color="#6c757d"
              style={{ display: "block", margin: "1.5rem auto 0" }}
            />
            <h1>Cadastro em manutenção</h1>
            <p>
              Nosso sistema está passando por uma manutenção para atualização.
              Em breve o cadastro de novas contas será liberado novamente.
            </p>
            <p style={{ marginTop: "1rem" }}>
              Se você já possui uma conta, seu acesso continua normalmente.
            </p>
          </S.Header>
        </S.FormWrapper>
      </S.PageContainer>
    );
  }

  return (
    <S.PageContainer>
      <S.FormWrapper onSubmit={handleCadastro}>
        <S.BackButton
          type="button"
          onClick={() => (window.location.href = "/")}
        >
          <FiArrowLeft /> Voltar
        </S.BackButton>
        <S.Header>
          <S.Logo src="/logo-golden.png" alt="Golden Brasil" />
          <h1>Crie sua Conta</h1>
          <p>Preencha os campos abaixo para iniciar sua jornada.</p>
        </S.Header>
        <S.Section>
          <S.SectionTitle>1. Identificação</S.SectionTitle>
          <S.InputGroup>
            <label>Tipo de Documento</label>
            <S.DocTypeSelector>
              <S.DocTypeButton
                type="button"
                active={documentType === "CPF"}
                onClick={() => handleDocTypeChange("CPF")}
              >
                <FiUser /> Pessoa Física (CPF)
              </S.DocTypeButton>
              <S.DocTypeButton
                type="button"
                active={documentType === "CNPJ"}
                onClick={() => handleDocTypeChange("CNPJ")}
              >
                <FiBriefcase /> Pessoa Jurídica (CNPJ)
              </S.DocTypeButton>
              <S.DocTypeButton
                type="button"
                active={documentType === "Passport"}
                onClick={() => handleDocTypeChange("Passport")}
              >
                <FiGlobe /> Passaporte
              </S.DocTypeButton>
            </S.DocTypeSelector>
          </S.InputGroup>
          <S.FormGrid>
            <S.InputGroup style={{ gridColumn: "1 / -1" }}>
              <label>{documentType}</label>
              <S.UsernameInputWrapper>
                <input
                  type="text"
                  value={documentValue}
                  onChange={handleDocumentInputChange}
                  placeholder={
                    documentType === "Passport"
                      ? "Digite o número do passaporte"
                      : "Digite e aguarde a validação automática"
                  }
                  required
                />
                {documentType !== "Passport" && documentStatus !== "idle" && (
                  <S.StatusIndicator status={documentStatus}>
                    {documentStatus === "validating" && (
                      <FiLoader className="spinner" />
                    )}
                    {documentStatus === "valid" && <FiCheckCircle />}
                    {documentStatus === "invalid" && <FiAlertCircle />}
                  </S.StatusIndicator>
                )}
              </S.UsernameInputWrapper>
              {documentError && (
                <S.ErrorMessage>{documentError}</S.ErrorMessage>
              )}
            </S.InputGroup>

            {(documentType === "CPF" || documentType === "CNPJ") && (
              <>
                <S.InputGroup>
                  <label>
                    {documentType === "CNPJ" ? "Razão Social" : "Nome Completo"}
                  </label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    readOnly={!isManualEntryEnabled}
                    required
                    placeholder={
                      isManualEntryEnabled
                        ? "Digite o nome"
                        : "Será preenchido automaticamente"
                    }
                  />
                </S.InputGroup>
                <S.InputGroup>
                  <label>
                    {documentType === "CNPJ"
                      ? "Data de Abertura"
                      : "Data de Nascimento"}
                  </label>
                  <input
                    type="date"
                    value={dataNascimento}
                    onChange={(e) => setDataNascimento(e.target.value)}
                    readOnly={!isManualEntryEnabled}
                    required
                  />
                  {ageError && <S.ErrorMessage>{ageError}</S.ErrorMessage>}
                </S.InputGroup>
              </>
            )}

            {documentType === "Passport" && (
              <>
                <S.InputGroup>
                  <label>Nome Completo</label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    required
                  />
                </S.InputGroup>
                <S.InputGroup>
                  <label>Data de Nascimento</label>
                  <S.InfoMessage>
                    É necessário ter 18 anos ou mais.
                  </S.InfoMessage>
                  <input
                    type="date"
                    value={dataNascimento}
                    onChange={(e) => setDataNascimento(e.target.value)}
                    required
                  />
                  {ageError && <S.ErrorMessage>{ageError}</S.ErrorMessage>}
                </S.InputGroup>
              </>
            )}
          </S.FormGrid>
        </S.Section>

        <S.Section>
          <S.SectionTitle>2. Contato e Endereço</S.SectionTitle>
          <S.FormGrid>
            <S.InputGroup>
              <label>Telefone</label>
              <input
                type="text"
                value={telefone}
                onChange={(e) => setTelefone(formatTelefone(e.target.value))}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>País</label>
              <input
                type="text"
                value={pais}
                onChange={(e) => setPais(e.target.value)}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>CEP</label>
              <input
                type="text"
                value={cep}
                onChange={(e) => setCep(formatCEP(e.target.value))}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>Endereço</label>
              <input
                type="text"
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>Número</label>
              <input
                type="text"
                value={enderecoNumber}
                onChange={(e) => setEnderecoNumber(e.target.value)}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>Bairro</label>
              <input
                type="text"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>Cidade</label>
              <input
                type="text"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>Estado</label>
              <input
                type="text"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
                required
              />
            </S.InputGroup>
          </S.FormGrid>
        </S.Section>
        <S.Section>
          <S.SectionTitle>3. Acesso à Plataforma</S.SectionTitle>
          <S.FormGrid>
            <S.InputGroup>
              <label>E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </S.InputGroup>
            <S.InputGroup>
              <label>Nome de Usuário</label>
              <S.UsernameInputWrapper>
                <input
                  type="text"
                  value={usuario}
                  onChange={(e) =>
                    setUsuario(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))
                  }
                  required
                />
                {usernameStatus.status !== "idle" && (
                  <S.StatusIndicator status={usernameStatus.status}>
                    {usernameStatus.status === "checking" && (
                      <FiLoader className="spinner" />
                    )}
                    {usernameStatus.status === "available" && <FiCheckCircle />}
                    {usernameStatus.status === "taken" && <FiAlertCircle />}
                    {usernameStatus.status === "error" && <FiAlertCircle />}
                    <span>{usernameStatus.message}</span>
                  </S.StatusIndicator>
                )}
              </S.UsernameInputWrapper>
            </S.InputGroup>

            {/* Campo Senha com Botão Ver/Ocultar */}
            <S.InputGroup>
              <label>Senha</label>
              <S.PasswordWrapper>
                <input
                  type={showPassword ? "text" : "password"}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                />
                <S.EyeButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </S.EyeButton>
              </S.PasswordWrapper>
            </S.InputGroup>

            {/* Campo Confirmar Senha com Botão Ver/Ocultar */}
            <S.InputGroup>
              <label>Confirmar Senha</label>
              <S.PasswordWrapper>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  required
                />
                <S.EyeButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </S.EyeButton>
              </S.PasswordWrapper>
            </S.InputGroup>
          </S.FormGrid>
        </S.Section>

        <S.SubmitButton
          type="submit"
          disabled={
            usernameStatus.status === "checking" ||
            usernameStatus.status === "taken" ||
            (documentType !== "Passport" && documentStatus !== "valid") ||
            !!ageError
          }
        >
          Finalizar Cadastro
        </S.SubmitButton>
      </S.FormWrapper>
      {resposta && (
        <MessageBox
          message={resposta.message}
          type={resposta.type}
          onClose={() => setResposta(null)}
        />
      )}
    </S.PageContainer>
  );
}
